/** Event data on the client: loading, caching and small helpers shared by the calendar, home and admin. */
import { api } from "./api";
import { S, isStaff } from "./state";
import { typeInfo } from "../shared/constants";
import type { EventView, LocationView, TemplateView } from "../shared/types";

export const E: {
  byId: Map<number, EventView>;
  years: Set<number>;
  locations: LocationView[] | null;
  templates: TemplateView[] | null;
} = { byId: new Map(), years: new Set(), locations: null, templates: null };

export function resetEvents() {
  E.byId.clear();
  E.years.clear();
  E.locations = null;
  E.templates = null;
}

const syOf = (day: string) => {
  const [y, m] = day.split("-").map(Number);
  return m >= 7 ? y : y - 1;
};

/** Loads a school year's events once. Returns true when it fetched something new. */
export async function ensureYear(day: string): Promise<boolean> {
  const sy = syOf(day);
  if (E.years.has(sy)) return false;
  if (!isStaff() && sy < S.me!.schoolYear) return false;
  E.years.add(sy);
  try {
    const list = await api<EventView[]>(`/api/events?from=${sy}-07-01&to=${sy + 1}-06-30`);
    for (const e of list) E.byId.set(e.id, e);
  } catch (e) {
    E.years.delete(sy);
    throw e;
  }
  return true;
}

export const put = (e: EventView) => {
  E.byId.set(e.id, e);
  return e;
};

export async function ensureStaffLists() {
  if (!isStaff()) return;
  if (!E.locations) E.locations = await api<LocationView[]>("/api/events/locations");
  if (!E.templates) E.templates = await api<TemplateView[]>("/api/events/templates");
}

export const allEvents = () => [...E.byId.values()].filter((e) => e.status !== "canceled");
export const sortEv = (a: EventView, b: EventView) => (a.date + a.start + a.title).localeCompare(b.date + b.start + b.title);
export const colorOf = (e: { type: string }) => typeInfo(e.type).color;

export const meId = () => S.me!.user.id;
export const mySignup = (e: EventView) => e.signups.find((s) => s.userId === meId());
export const leads = (e: EventView, kind: "PA" | "SA") => e.signups.filter((s) => s.kind === kind && !s.shadow);

/** mine / open / full / team / cx, from this viewer's point of view. Shadows never count as open spots. */
export function stateOf(e: EventView): "mine" | "open" | "full" | "team" | "cx" {
  if (e.status === "canceled") return "cx";
  if (e.type === "team") return "team";
  if (mySignup(e)) return "mine";
  const r = S.me!.user.roles;
  const openPA = e.pa - leads(e, "PA").length;
  const openSA = e.sa - leads(e, "SA").length;
  if (!r.pa && !r.sa) return openPA > 0 || openSA > 0 ? "open" : "full";
  return (r.pa && openPA > 0) || (r.sa && openSA > 0) ? "open" : "full";
}

export const shortTitle = (t: string) => t.replace(/Coffee & Conversation \[([^\s\]]+).*\]/, "Coffee $1").replace(" Group Tour", " Tour");
